import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, KeyRound, Mail, Cake, User as UserIcon, IdCard, ShieldCheck, Building2, Phone, Briefcase, Calendar, Camera, UploadCloud, MapPin, Wallet, Landmark, AlertTriangle, Sparkles, Droplet } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import UserAvatar, { TIER_TINTS } from "@/components/app/UserAvatar";
import { PushSettingsCard } from "@/components/app/PushNotificationControls";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");

const ReadOnlyRow = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3 py-3 border-b border-zinc-100 last:border-0" data-testid={testid}>
    <Icon className="w-4 h-4 text-zinc-400 mt-0.5" />
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-900 mt-0.5">{value || "—"}</div>
    </div>
  </div>
);

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [permanentAddress, setPermanentAddress] = useState("");
  const [pfNumber, setPfNumber] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [passportPhotoPath, setPassportPhotoPath] = useState("");
  const [aadharNumber, setAadharNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [uniformCategories, setUniformCategories] = useState([]);
  const [uniformSizes, setUniformSizes] = useState({});
  const [savingUniform, setSavingUniform] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [savingIdCard, setSavingIdCard] = useState(false);
  const passportFileRef = useRef(null);
  const [bank, setBank] = useState({ account_holder_name: "", account_number: "", ifsc: "", bank_name: "" });
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [loading, setLoading] = useState(true);
  const avatarFileRef = useRef(null);
  const avatarCameraRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setProfile(data);
      setName(data.name || "");
      setEmail(data.email || "");
      setDob(data.profile?.date_of_birth || "");
      setPermanentAddress(data.profile?.permanent_address || "");
      setPfNumber(data.profile?.pf_number || "");
      setBloodGroup(data.profile?.blood_group || "");
      setPassportPhotoPath(data.profile?.passport_photo_path || "");
      setAadharNumber(data.profile?.aadhar_number || "");
      setPanNumber(data.profile?.pan_number || "");
      setUniformSizes(data.profile?.uniform_sizes || {});
      setBank({
        account_holder_name: data.profile?.bank_account?.account_holder_name || "",
        account_number: data.profile?.bank_account?.account_number || "",
        ifsc: data.profile?.bank_account?.ifsc || "",
        bank_name: data.profile?.bank_account?.bank_name || "",
      });
    } catch (e) {
      logger.error("Profile load failed", e);
      toast.error("Could not load your profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Load admin-managed uniform categories so the profile shows the right rows.
  useEffect(() => {
    let cancelled = false;
    api.get("/uniform-categories").then(({ data }) => {
      if (!cancelled) setUniformCategories(data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load this employee's own uniform issuance log (read-only).
  const [myIssuances, setMyIssuances] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/uniform-issuances/me").then(({ data }) => {
      if (!cancelled) setMyIssuances(data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const saveUniformSizes = async () => {
    setSavingUniform(true);
    try {
      // Drop empty values so saved keys reflect only chosen sizes
      const cleaned = {};
      Object.entries(uniformSizes || {}).forEach(([k, v]) => {
        const s = (v || "").trim();
        if (s) cleaned[k] = s;
      });
      await api.put("/me/profile", { uniform_sizes: cleaned });
      setProfile((p) => ({ ...p, profile: { ...(p?.profile || {}), uniform_sizes: cleaned } }));
      toast.success("Uniform sizes saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingUniform(false);
    }
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    const payload = {};
    if (name && name.trim() !== (profile?.name || "")) {
      payload.name = name.trim();
    }
    if (email && email.trim().toLowerCase() !== (profile?.email || "").toLowerCase()) {
      payload.email = email.trim().toLowerCase();
    }
    if (dob && dob !== (profile?.profile?.date_of_birth || "")) {
      payload.date_of_birth = dob;
    }
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSavingDetails(true);
    try {
      const { data } = await api.put("/me/credentials", payload);
      setProfile(data);
      if (setUser && user) {
        setUser({ ...user, name: data.name, email: data.email });
      }
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSavingDetails(false);
    }
  };

  const saveFinancial = async (e) => {
    e.preventDefault();
    const payload = {};
    if (permanentAddress.trim() !== (profile?.profile?.permanent_address || "")) {
      payload.permanent_address = permanentAddress.trim();
    }
    if (pfNumber.trim() !== (profile?.profile?.pf_number || "")) {
      payload.pf_number = pfNumber.trim();
    }
    const existingBank = profile?.profile?.bank_account || {};
    const bankDiff = {};
    for (const k of ["account_holder_name", "account_number", "ifsc", "bank_name"]) {
      if ((bank[k] || "").trim() !== (existingBank[k] || "")) bankDiff[k] = (bank[k] || "").trim();
    }
    if (Object.keys(bankDiff).length > 0) payload.bank_account = bankDiff;
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSavingFinancial(true);
    try {
      await api.put("/me/profile", payload);
      const { data: me } = await api.get("/auth/me");
      setProfile(me);
      toast.success("Address & banking details updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSavingFinancial(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setSavingPwd(true);
    try {
      await api.put("/me/credentials", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      toast.success("Password changed successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setSavingPwd(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/me/profile", { avatar_path: up.path });
      setProfile((p) => ({ ...p, avatar_path: up.path, profile: { ...(p?.profile || {}), avatar_path: up.path } }));
      if (setUser && user) setUser({ ...user, avatar_path: up.path });
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const uploadPassportPhoto = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Passport photo must be under 2 MB"); return; }
    setUploadingPassport(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/me/profile", { passport_photo_path: up.path });
      setPassportPhotoPath(up.path);
      setProfile((p) => ({ ...p, profile: { ...(p?.profile || {}), passport_photo_path: up.path } }));
      toast.success("Passport photo uploaded — ready for your ID card");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPassport(false);
    }
  };

  const saveIdCardDetails = async () => {
    const cur = profile?.profile || {};
    const payload = {};
    if ((bloodGroup || "") !== (cur.blood_group || "")) payload.blood_group = bloodGroup || null;
    if ((aadharNumber || "").trim() !== (cur.aadhar_number || "")) payload.aadhar_number = (aadharNumber || "").trim() || null;
    if ((panNumber || "").trim().toUpperCase() !== (cur.pan_number || "")) payload.pan_number = (panNumber || "").trim().toUpperCase() || null;
    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }
    // Light client-side validation (server is permissive — these are optional)
    if (payload.aadhar_number && !/^\d{12}$/.test(payload.aadhar_number.replace(/\s/g, ""))) {
      toast.error("Aadhar number must be 12 digits");
      return;
    }
    if (payload.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(payload.pan_number)) {
      toast.error("PAN must be 10 chars (e.g. ABCDE1234F)");
      return;
    }
    setSavingIdCard(true);
    try {
      await api.put("/me/profile", payload);
      setProfile((p) => ({ ...p, profile: { ...(p?.profile || {}), ...payload } }));
      toast.success("ID card details updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSavingIdCard(false);
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!profile) return null;

  const p = profile.profile || {};
  const role = profile.role?.replace("_", " ");

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <span className="label-overline">Account</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">My Profile</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-lg">
          Update your photo, name, email, password, date of birth, address, bank account and PF details. Everything else is managed by your administrator.
        </p>
      </div>

      {/* Avatar + conduct tier banner */}
      <div
        className={`bg-white border border-zinc-200 rounded-md p-6 flex items-center gap-5 ${TIER_TINTS[profile.conduct_tier]?.row || ""}`}
        data-testid="avatar-card"
        data-conduct-tier={profile.conduct_tier || "none"}
      >
        <UserAvatar user={profile} size={96} className="border-2" />
        <div className="flex-1 min-w-0">
          <span className="label-overline">Profile photo</span>
          <div className="font-display text-xl font-bold tracking-tight mt-0.5">{profile.name}</div>
          {profile.conduct_tier && profile.conduct_tier !== "none" && (
            <div
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest border rounded-full px-2 py-0.5 mt-1.5 ${TIER_TINTS[profile.conduct_tier]?.pill || ""}`}
              data-testid="profile-conduct-pill"
              data-conduct-tier={profile.conduct_tier}
            >
              {profile.conduct_tier === "appreciation" ? <Sparkles className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
              {TIER_TINTS[profile.conduct_tier]?.label}
            </div>
          )}
          <p className="text-xs text-zinc-500 mt-1">Take a fresh photo or upload one from your device — appears across the app.</p>
          <input
            ref={avatarFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadAvatar(f); }}
            data-testid="avatar-file-input"
          />
          <input
            ref={avatarCameraRef}
            type="file"
            accept="image/*"
            capture="user"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadAvatar(f); }}
            data-testid="avatar-camera-input"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => avatarCameraRef.current?.click()} disabled={uploadingAvatar} data-testid="avatar-camera-btn">
              {uploadingAvatar ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
              Take photo
            </Button>
            <Button variant="outline" size="sm" onClick={() => avatarFileRef.current?.click()} disabled={uploadingAvatar} data-testid="avatar-upload-btn">
              <UploadCloud className="w-3.5 h-3.5 mr-1" /> Upload
            </Button>
          </div>
        </div>
      </div>

      {/* Browser push settings */}
      <PushSettingsCard />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Editable */}
        <div className="space-y-6">
          <form onSubmit={saveDetails} className="bg-white border border-zinc-200 rounded-md p-6" data-testid="edit-details-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="label-overline">Editable</span>
                <h3 className="font-display text-xl font-bold tracking-tight mt-1">Personal details</h3>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Full name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-11"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11"
                  data-testid="profile-email-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Date of birth</Label>
                <Input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  className="mt-2 h-11"
                  data-testid="profile-dob-input"
                />
              </div>
              <Button type="submit" disabled={savingDetails} className="bg-zinc-950 hover:bg-zinc-800" data-testid="profile-save-details">
                {savingDetails ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save changes
              </Button>
            </div>
          </form>

          {/* Address, Bank account, PF — saved together to /me/profile */}
          <form onSubmit={saveFinancial} className="bg-white border border-zinc-200 rounded-md p-6" data-testid="edit-financial-card">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="w-4 h-4 text-zinc-500" />
              <h3 className="font-display text-xl font-bold tracking-tight">Address &amp; banking</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> Permanent address
                </Label>
                <Input
                  type="text"
                  value={permanentAddress}
                  onChange={(e) => setPermanentAddress(e.target.value)}
                  placeholder="House no, street, city, state, PIN"
                  className="mt-2 h-11"
                  data-testid="profile-address-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" /> PF number
                </Label>
                <Input
                  type="text"
                  value={pfNumber}
                  onChange={(e) => setPfNumber(e.target.value)}
                  placeholder="e.g. AB/CDE/0123456/000/0000001"
                  className="mt-2 h-11 font-mono text-sm"
                  data-testid="profile-pf-input"
                />
              </div>
              <div className="pt-2 border-t border-zinc-100">
                <div className="text-xs font-semibold tracking-wide uppercase text-zinc-700 mb-2 flex items-center gap-1.5">
                  <Landmark className="w-3.5 h-3.5" /> Bank account
                </div>
                <div className="space-y-3">
                  <Input
                    type="text"
                    value={bank.account_holder_name}
                    onChange={(e) => setBank((b) => ({ ...b, account_holder_name: e.target.value }))}
                    placeholder="Account holder name"
                    className="h-11"
                    data-testid="profile-bank-name-input"
                  />
                  <Input
                    type="text"
                    value={bank.bank_name}
                    onChange={(e) => setBank((b) => ({ ...b, bank_name: e.target.value }))}
                    placeholder="Bank name (e.g. HDFC Bank)"
                    className="h-11"
                    data-testid="profile-bank-bankname-input"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      type="text"
                      value={bank.account_number}
                      onChange={(e) => setBank((b) => ({ ...b, account_number: e.target.value.replace(/\s/g, "") }))}
                      placeholder="Account number"
                      className="h-11 font-mono text-sm"
                      data-testid="profile-bank-acct-input"
                    />
                    <Input
                      type="text"
                      value={bank.ifsc}
                      onChange={(e) => setBank((b) => ({ ...b, ifsc: e.target.value.toUpperCase() }))}
                      placeholder="IFSC code"
                      className="h-11 font-mono text-sm uppercase"
                      maxLength={11}
                      data-testid="profile-bank-ifsc-input"
                    />
                  </div>
                </div>
              </div>
              <Button type="submit" disabled={savingFinancial} className="bg-zinc-950 hover:bg-zinc-800" data-testid="profile-save-financial">
                {savingFinancial ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save details
              </Button>
            </div>
          </form>

          {/* ID Card details — passport photo + blood group */}
          <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="edit-idcard-card">
            <div className="flex items-center gap-2 mb-1">
              <IdCard className="w-4 h-4 text-zinc-500" />
              <h3 className="font-display text-xl font-bold tracking-tight">ID card details</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Used when HR issues your physical ID card. Upload a 1×1 passport-style photo and pick your blood group.
            </p>
            <div className="flex items-start gap-5">
              <div className="shrink-0">
                <div
                  className="w-28 h-32 rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center overflow-hidden"
                  data-testid="passport-photo-preview"
                >
                  {passportPhotoPath ? (
                    <img
                      src={`${API}/files?path=${encodeURIComponent(passportPhotoPath)}`}
                      alt="Passport"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-[10px] text-zinc-400 text-center px-2 leading-tight">
                      No photo<br/>1×1 portrait
                    </div>
                  )}
                </div>
                <input
                  ref={passportFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPassportPhoto(f); e.target.value = ""; }}
                  data-testid="passport-photo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 w-28 justify-center"
                  onClick={() => passportFileRef.current?.click()}
                  disabled={uploadingPassport}
                  data-testid="upload-passport-btn"
                >
                  {uploadingPassport ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <UploadCloud className="w-3.5 h-3.5 mr-1" />}
                  {passportPhotoPath ? "Replace" : "Upload"}
                </Button>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700 flex items-center gap-1.5">
                    <Droplet className="w-3.5 h-3.5" /> Blood group
                  </Label>
                  <Select value={bloodGroup || "unset"} onValueChange={(v) => setBloodGroup(v === "unset" ? "" : v)}>
                    <SelectTrigger className="mt-2 h-11" data-testid="profile-bloodgroup-select">
                      <SelectValue placeholder="Pick your blood group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unset">Prefer not to say</SelectItem>
                      {BLOOD_GROUPS.map((bg) => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700 flex items-center gap-1.5">
                      <IdCard className="w-3.5 h-3.5" /> Aadhar number <span className="text-zinc-400 normal-case text-[10px]">(optional)</span>
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={aadharNumber}
                      onChange={(e) => setAadharNumber(e.target.value.replace(/[^\d\s]/g, "").slice(0, 14))}
                      placeholder="12-digit Aadhar"
                      className="mt-2 h-11 font-mono text-sm"
                      data-testid="profile-aadhar-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700 flex items-center gap-1.5">
                      <IdCard className="w-3.5 h-3.5" /> PAN number <span className="text-zinc-400 normal-case text-[10px]">(optional)</span>
                    </Label>
                    <Input
                      type="text"
                      value={panNumber}
                      onChange={(e) => setPanNumber(e.target.value.toUpperCase().slice(0, 10))}
                      placeholder="ABCDE1234F"
                      className="mt-2 h-11 font-mono text-sm uppercase"
                      maxLength={10}
                      data-testid="profile-pan-input"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={saveIdCardDetails}
                  disabled={savingIdCard}
                  className="bg-zinc-950 hover:bg-zinc-800"
                  data-testid="profile-save-idcard"
                >
                  {savingIdCard ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save ID card details
                </Button>
              </div>
            </div>
          </div>

          {/* Uniform sizes — admin manages the categories under /uniform-categories */}
          {uniformCategories.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="edit-uniform-card">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-zinc-500" />
                <h3 className="font-display text-xl font-bold tracking-tight">Uniform sizes</h3>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                Helps HR order the right sizes when issuing your uniform. Leave blank if you'd rather not say.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {uniformCategories.map((c) => {
                  const value = uniformSizes[c.key] || "";
                  const hasOptions = (c.options || []).length > 0;
                  return (
                    <div key={c.key}>
                      <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">{c.label}</Label>
                      {hasOptions ? (
                        <Select
                          value={value || "unset"}
                          onValueChange={(v) => setUniformSizes((s) => ({ ...s, [c.key]: v === "unset" ? "" : v }))}
                        >
                          <SelectTrigger className="mt-1.5 h-11" data-testid={`uniform-size-${c.key}`}>
                            <SelectValue placeholder="Pick a size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unset">Prefer not to say</SelectItem>
                            {c.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={value}
                          onChange={(e) => setUniformSizes((s) => ({ ...s, [c.key]: e.target.value }))}
                          placeholder="Type your size"
                          className="mt-1.5 h-11"
                          data-testid={`uniform-size-${c.key}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <Button
                type="button"
                onClick={saveUniformSizes}
                disabled={savingUniform}
                className="bg-zinc-950 hover:bg-zinc-800 mt-4"
                data-testid="profile-save-uniform"
              >
                {savingUniform ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save uniform sizes
              </Button>
            </div>
          )}

          {/* Read-only: my uniform issuance log */}
          {myIssuances.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="my-uniform-issuances">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-zinc-500" />
                <h3 className="font-display text-xl font-bold tracking-tight">Uniforms received</h3>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                Sets handed to you so far. If anything looks wrong, raise it with your supervisor.
              </p>
              {/* Per-category totals */}
              {(() => {
                const totals = {};
                for (const it of myIssuances) {
                  totals[it.category_label] = (totals[it.category_label] || 0) + (it.quantity || 0);
                }
                const entries = Object.entries(totals);
                return (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {entries.map(([label, q]) => (
                      <span key={label} className="inline-flex items-center gap-1.5 text-xs bg-zinc-100 rounded px-2 py-1">
                        <span className="text-zinc-500">{label}</span>
                        <span className="font-bold tabular-nums">{q}</span>
                      </span>
                    ))}
                  </div>
                );
              })()}
              <div className="border border-zinc-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                      <th className="py-2 px-3">Date</th>
                      <th className="py-2 px-3">Category</th>
                      <th className="py-2 px-3">Size</th>
                      <th className="py-2 px-3 text-right">Qty</th>
                      <th className="py-2 px-3">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myIssuances.map((it) => (
                      <tr key={it.id} className="border-b border-zinc-100 last:border-0">
                        <td className="py-2 px-3 font-mono text-xs">{it.issued_at}</td>
                        <td className="py-2 px-3">{it.category_label}</td>
                        <td className="py-2 px-3 font-mono">{it.size || "—"}</td>
                        <td className="py-2 px-3 text-right tabular-nums font-bold">{it.quantity}</td>
                        <td className="py-2 px-3 text-xs text-zinc-600">{it.remarks || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <form onSubmit={savePassword} className="bg-white border border-zinc-200 rounded-md p-6" data-testid="edit-password-card">
            <div className="flex items-center gap-2 mb-4">
              <KeyRound className="w-4 h-4 text-zinc-500" />
              <h3 className="font-display text-xl font-bold tracking-tight">Change password</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Current password</Label>
                <Input
                  type="password"
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  required
                  className="mt-2 h-11"
                  data-testid="profile-current-pwd"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">New password</Label>
                <Input
                  type="password"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  required
                  minLength={6}
                  className="mt-2 h-11"
                  data-testid="profile-new-pwd"
                />
                <p className="text-[11px] text-zinc-500 mt-1">At least 6 characters.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-700">Confirm new password</Label>
                <Input
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required
                  className="mt-2 h-11"
                  data-testid="profile-confirm-pwd"
                />
                {confirmPwd && newPwd && confirmPwd !== newPwd && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="profile-pwd-mismatch">
                    Passwords don't match.
                  </p>
                )}
              </div>
              <Button type="submit" disabled={savingPwd || !currentPwd || !newPwd || !confirmPwd} className="bg-zinc-950 hover:bg-zinc-800" data-testid="profile-save-password">
                {savingPwd ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Update password
              </Button>
            </div>
          </form>
        </div>

        {/* Read-only details (managed by admin) */}
        <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="readonly-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="label-overline">Managed by admin</span>
              <h3 className="font-display text-xl font-bold tracking-tight mt-1">Employment details</h3>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mb-4">These are managed by your manager. Reach out if any of these need to change.</p>
          <ReadOnlyRow icon={IdCard} label="Employee ID" value={profile.employee_id} testid="ro-emp-id" />
          <ReadOnlyRow icon={Phone} label="Phone" value={p.phone} testid="ro-phone" />
          <ReadOnlyRow icon={ShieldCheck} label="Role" value={role} testid="ro-role" />
          <ReadOnlyRow icon={Briefcase} label="Department" value={profile.department} testid="ro-department" />
          <ReadOnlyRow icon={ShieldCheck} label="Employment status" value={(profile.employment_status || "probation").replace(/\b\w/g, (c) => c.toUpperCase())} testid="ro-status" />
          <ReadOnlyRow icon={Calendar} label="Date of joining" value={fmtDate(profile.date_of_joining)} testid="ro-doj" />
          <ReadOnlyRow icon={Building2} label="Properties" value={profile.property_ids?.length ? `${profile.property_ids.length} assigned` : "—"} testid="ro-properties" />
        </div>
      </div>
    </div>
  );
}
